import { useState, useEffect } from 'react'
import { POI, RouteData } from '@cya-routes/shared'
import { googlePlacesService } from '../../services/googlePlacesService'
import { getPOITypeById, getPOICategoryById } from '../../data/poiTypes'
import { parseTextWithLinks } from '../../utils/textFormatting'
import { ChevronLeft, ChevronRight, X, MapPin, ArrowUpRight, Route } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

interface CompactPOIModalProps {
  poi: POI | null
  isOpen: boolean
  onClose: () => void
  routes: RouteData[]
  embedMode?: boolean
}

export function CompactPOIModal({ poi, isOpen, onClose, routes, embedMode = false }: CompactPOIModalProps) {
  const [googlePlaceData, setGooglePlaceData] = useState<any>(null)
  const [isLoadingPlaceData, setIsLoadingPlaceData] = useState(false)
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0)
  const [expandedSection, setExpandedSection] = useState<string | null>(null)
  const [viewportScale, setViewportScale] = useState(1)
  const navigate = useNavigate()

  // Update viewport scale based on window size
  useEffect(() => {
    const updateScale = () => {
      // Responsive scaling based on screen width
      let scale = 1 // Default scale for desktop
      
      if (window.innerWidth < 640) { // Mobile phones
        scale = 0.65
      } else if (window.innerWidth < 768) { // Small tablets
        scale = 0.75
      } else if (window.innerWidth < 1024) { // Tablets
        scale = 0.85
      }
      
      // Apply additional 30% reduction for embed mode
      if (embedMode) {
        scale = scale * 0.7
      }
      
      setViewportScale(scale)
      console.log('🔍 POI Modal scale:', scale, 'at width:', window.innerWidth, 'embedMode:', embedMode)
    }

    updateScale()
    window.addEventListener('resize', updateScale)
    return () => window.removeEventListener('resize', updateScale)
  }, [embedMode])

  // Fetch Google Places data when modal opens and POI has a Google Place ID
  useEffect(() => {
    if (isOpen && poi?.googlePlaceId && !googlePlaceData) {
      const fetchGooglePlaceData = async () => {
        setIsLoadingPlaceData(true)
        try {
          const placeDetails = await googlePlacesService.getPlaceDetails(poi.googlePlaceId)
          if (placeDetails) {
            setGooglePlaceData(placeDetails)
          } else {
            setGooglePlaceData({ error: 'No place details found' })
          }
        } catch (error) {
          console.error('❌ Error fetching Google Places data:', error)
          setGooglePlaceData({ error: 'Failed to fetch place details' })
        } finally {
          setIsLoadingPlaceData(false)
        }
      }
      
      fetchGooglePlaceData()
    }
  }, [isOpen, poi?.googlePlaceId, googlePlaceData])

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setGooglePlaceData(null)
      setCurrentPhotoIndex(0)
      setExpandedSection(null)
    }
  }, [isOpen])

  if (!isOpen || !poi) return null

  console.log('🎯 POI Modal rendering with scale:', viewportScale)

  const poiType = getPOITypeById(poi.type)
  const poiCategory = poiType ? getPOICategoryById(poiType.category) : null
  const IconComponent = poiType?.icon

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section)
  }

  const getLocationString = () => {
    if (googlePlaceData?.formatted_address) {
      return googlePlaceData.formatted_address
    }
    const route = routes.find(r => r.id === poi.routeId) || routes[0]
    if (route?.location?.city && route?.location?.region) {
      return `${route.location.city}, ${route.location.region}`
    } else if (route?.location?.city && route?.location?.state) {
      return `${route.location.city}, ${route.location.state}`
    } else {
      return `${poi.coordinates.lat.toFixed(6)}, ${poi.coordinates.lng.toFixed(6)}`
    }
  }

  return (
    <>
      {/* Backdrop - click to close */}
      <div 
        className={embedMode ? 
          "absolute inset-0 bg-black/20 z-40" : 
          "fixed inset-0 bg-black/20 z-40"
        } 
        onClick={onClose}
      />
      
      {/* Compact Modal - positioned on the right side */}
      <div 
        className={embedMode ? 
          "absolute bg-gray-800 rounded-xl shadow-2xl border border-gray-700 z-50 flex flex-col overflow-hidden" :
          "fixed bg-gray-800 rounded-xl shadow-2xl border border-gray-700 z-50 flex flex-col overflow-hidden"
        }
        style={{ 
          transform: `scale(${viewportScale})`,
          transformOrigin: 'top right',
          right: embedMode ? '48px' : '16px', // Match RoutePopupCard's right-12 (48px) for embed
          top: embedMode ? '80px' : '80px', // Match RoutePopupCard's top-20 (80px)
          width: '384px', // 96 * 4 = 384px
          maxHeight: 'calc(100vh - 160px)'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {IconComponent && (
              <div 
                className="flex-shrink-0 w-8 h-8 rounded flex items-center justify-center"
                style={{ 
                  backgroundColor: poiCategory?.id === 'city-town' 
                    ? '#273c7520' 
                    : `${poiCategory?.color || '#6b7280'}20` 
                }}
              >
                <IconComponent 
                  className="w-4 h-4" 
                  style={{ 
                    color: poiCategory?.id === 'city-town' 
                      ? '#273c75' 
                      : (poiCategory?.color || '#6b7280')
                  }}
                />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-semibold text-white truncate">
                {poi.name || poiType?.name || 'Point of Interest'}
              </h3>
              <p className="text-sm text-gray-400">{poiType?.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Photo Slider (if available) */}
          {googlePlaceData?.photos && googlePlaceData.photos.length > 0 && (
            <div className="relative h-48 bg-gray-900">
              {googlePlaceData.photos.map((photo: any, index: number) => {
                const photoUrl = photo.url || 
                  (photo.photo_reference ? 
                    `https://maps.googleapis.com/maps/api/place/photo?maxwidth=400&photoreference=${photo.photo_reference}&key=${import.meta.env.VITE_GOOGLE_PLACES_API_KEY}` : 
                    '')
                
                return (
                  <img
                    key={index}
                    src={photoUrl}
                    alt={`${googlePlaceData.name} photo ${index + 1}`}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
                      index === currentPhotoIndex ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                )
              })}
              
              {/* Photo navigation */}
              {googlePlaceData.photos.length > 1 && (
                <>
                  <button
                    onClick={() => setCurrentPhotoIndex(prev => prev > 0 ? prev - 1 : googlePlaceData.photos.length - 1)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 p-1 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4 text-white" />
                  </button>
                  <button
                    onClick={() => setCurrentPhotoIndex(prev => prev < googlePlaceData.photos.length - 1 ? prev + 1 : 0)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
                  >
                    <ChevronRight className="w-4 h-4 text-white" />
                  </button>
                  
                  {/* Photo indicators */}
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                    {googlePlaceData.photos.map((_: any, index: number) => (
                      <button
                        key={index}
                        onClick={() => setCurrentPhotoIndex(index)}
                        className={`w-1.5 h-1.5 rounded-full transition-colors ${
                          index === currentPhotoIndex ? 'bg-white' : 'bg-white/40'
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="p-4 space-y-4">
            {/* Quick Info */}
            {poi.googlePlaceId && googlePlaceData && !googlePlaceData.error && (
              <div className="flex items-center gap-4 text-sm">
                {googlePlaceData.rating && (
                  <div className="flex items-center gap-1">
                    <span className="text-yellow-400">★</span>
                    <span className="text-white font-medium">
                      {googlePlaceData.rating.toFixed(1)}
                    </span>
                    <span className="text-gray-400">
                      ({googlePlaceData.user_ratings_total})
                    </span>
                  </div>
                )}
                
                {googlePlaceData.price_level !== undefined && (
                  <span className="text-green-400 font-bold">
                    {'$'.repeat(googlePlaceData.price_level)}
                  </span>
                )}
                
                {googlePlaceData.opening_hours?.open_now !== undefined && (
                  <div className="flex items-center gap-1.5">
                    <div className={`w-2 h-2 rounded-full ${
                      googlePlaceData.opening_hours.open_now ? 'bg-green-500' : 'bg-red-500'
                    }`} />
                    <span className="text-white text-sm">
                      {googlePlaceData.opening_hours.open_now ? 'Open' : 'Closed'}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Description */}
            {poi.description && (
              <div>
                <p className="text-gray-300 text-sm leading-relaxed">
                  {parseTextWithLinks(poi.description)}
                </p>
              </div>
            )}

            {/* Location */}
            <div className="flex items-start gap-2 text-sm">
              <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <p className="text-gray-300">{getLocationString()}</p>
            </div>

            {/* Quick Links */}
            {(googlePlaceData?.url || googlePlaceData?.website) && (
              <div className="flex gap-2">
                {googlePlaceData.url && (
                  <a
                    href={googlePlaceData.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-lg transition-colors"
                  >
                    <MapPin className="w-3.5 h-3.5" />
                    <span>Google Maps</span>
                  </a>
                )}
                
                {googlePlaceData.website && (
                  <a
                    href={googlePlaceData.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors"
                  >
                    <ArrowUpRight className="w-3.5 h-3.5" />
                    <span>Website</span>
                  </a>
                )}
              </div>
            )}

            {/* Linked Route */}
            {poi.linkedRouteId && poi.linkedRouteName && (
              <div className="border border-gray-700 rounded-lg overflow-hidden">
                <a
                  href={embedMode ? '#' : `/route/${poi.linkedRouteId}`}
                  target={embedMode ? undefined : "_blank"}
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    if (embedMode) {
                      e.preventDefault()
                    }
                  }}
                  className={`block w-full p-3 bg-gray-700/50 hover:bg-gray-700 transition-colors ${
                    embedMode ? 'cursor-not-allowed opacity-50' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-emerald-600/20 rounded-lg flex items-center justify-center">
                        <Route className="w-4 h-4 text-emerald-400" />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-medium text-white">Linked Route</p>
                        <p className="text-xs text-emerald-400">{poi.linkedRouteName}</p>
                      </div>
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-gray-400" />
                  </div>
                </a>
              </div>
            )}

            {/* Accordion Sections */}
            {googlePlaceData && !googlePlaceData.error && (
              <div className="space-y-2">
                {/* Contact Info */}
                {googlePlaceData.formatted_phone_number && (
                  <div className="border border-gray-700 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleSection('contact')}
                      className="w-full px-4 py-3 bg-gray-700/50 hover:bg-gray-700 transition-colors flex items-center justify-between"
                    >
                      <span className="text-white text-sm font-medium">Contact</span>
                      <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${
                        expandedSection === 'contact' ? 'rotate-90' : ''
                      }`} />
                    </button>
                    {expandedSection === 'contact' && (
                      <div className="p-4 bg-gray-800/50">
                        <a 
                          href={`tel:${googlePlaceData.formatted_phone_number}`} 
                          className="text-blue-400 hover:text-blue-300 text-sm"
                        >
                          {googlePlaceData.formatted_phone_number}
                        </a>
                      </div>
                    )}
                  </div>
                )}

                {/* Opening Hours */}
                {googlePlaceData.opening_hours?.weekday_text && googlePlaceData.opening_hours.weekday_text.length > 0 && (
                  <div className="border border-gray-700 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleSection('hours')}
                      className="w-full px-4 py-3 bg-gray-700/50 hover:bg-gray-700 transition-colors flex items-center justify-between"
                    >
                      <span className="text-white text-sm font-medium">Hours</span>
                      <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${
                        expandedSection === 'hours' ? 'rotate-90' : ''
                      }`} />
                    </button>
                    {expandedSection === 'hours' && (
                      <div className="p-4 bg-gray-800/50 space-y-1">
                        {googlePlaceData.opening_hours.weekday_text.map((day: string, index: number) => (
                          <div key={index} className="text-gray-300 text-xs font-mono">
                            {day}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Reviews */}
                {googlePlaceData.reviews && googlePlaceData.reviews.length > 0 && (
                  <div className="border border-gray-700 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleSection('reviews')}
                      className="w-full px-4 py-3 bg-gray-700/50 hover:bg-gray-700 transition-colors flex items-center justify-between"
                    >
                      <span className="text-white text-sm font-medium">
                        Reviews ({googlePlaceData.reviews.length})
                      </span>
                      <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${
                        expandedSection === 'reviews' ? 'rotate-90' : ''
                      }`} />
                    </button>
                    {expandedSection === 'reviews' && (
                      <div className="p-4 bg-gray-800/50 space-y-3">
                        {googlePlaceData.reviews.slice(0, 3).map((review: any, index: number) => (
                          <div key={index} className="pb-3 last:pb-0 border-b last:border-0 border-gray-700">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-medium text-white">{review.author_name}</span>
                              <div className="flex items-center gap-0.5">
                                <span className="text-yellow-400 text-xs">★</span>
                                <span className="text-gray-400 text-xs">{review.rating}</span>
                              </div>
                            </div>
                            <p className="text-gray-300 text-xs leading-relaxed">
                              {review.text.length > 100 ? `${review.text.substring(0, 100)}...` : review.text}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Loading state */}
            {isLoadingPlaceData && (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}